import UrlFetcher from 'app/containers/UrlFetcher';
import React, { useCallback, useState } from 'react';
import i18next from 'i18next';
import type {
  AnyRestaruant,
  TDeliveryPricing,
  TDeliveryPricingDTO,
} from 'types';
import DeliveriesPricingsList from './DeliveriesPricingsList';
import APIService from 'services/api';
import { toast } from 'react-toastify';
import { getArrayWithoutObject } from 'utils/array';
import { CommonSection } from 'app/components/common';
import AddDeliveryPricingForm from './AddDeliveryPricingForm';
import Restaurant from 'utils/restaurant';

export type ViewRestaurantDeliveriesPricingsProps = {
  restaurant: AnyRestaruant;
};

const ViewRestaurantDeliveriesPricings: React.FC<
  ViewRestaurantDeliveriesPricingsProps
> = ({ restaurant }) => {
  const [loadingDeliveryPricings, setLoadingDeliveryPricings] = useState<
    number[]
  >([]);

  const isLoading = useCallback(
    (deliveryPricing: TDeliveryPricing) =>
      loadingDeliveryPricings.includes(deliveryPricing.id),
    [loadingDeliveryPricings]
  );

  if (!Restaurant.hasAnyDeliveries(restaurant)) {
    return (
      <CommonSection header={i18next.t('view-restaurant-deliveries-pricings.header')}>
        {i18next.t('view-restaurant-deliveries-pricings.not-applicable')}
      </CommonSection>
    );
  }

  return (
    <>
      <UrlFetcher<TDeliveryPricing[]>
        url={`/restaurants/${restaurant.id}/deliveries-pricings`}
      >
        {([deliveriesPricings, { setData }]) => {
          const handleAddDeliveryPricing = async (
            payload: TDeliveryPricingDTO
          ) => {
            return APIService.post(
              `/restaurants/${restaurant.id}/deliveries-pricings`,
              payload
            )
              .then((deliveryPricing) => {
                setData([...deliveriesPricings, deliveryPricing]);
                toast.success(i18next.t('view-restaurant-deliveries-pricings.add-success'));
                return deliveryPricing;
              })
              .catch(() => {
                toast.error(i18next.t('view-restaurant-deliveries-pricings.add-error'));
                return null;
              });
          };

          const handleRemoveDeliveryPricing = async (
            deliveryPricing: TDeliveryPricing
          ) => {
            setLoadingDeliveryPricings((prev) => [...prev, deliveryPricing.id]);
            return APIService.del(
              `/restaurants/${restaurant.id}/deliveries-pricings/${deliveryPricing.id}`
            )
              .then(() => {
                setLoadingDeliveryPricings((prev) =>
                  prev.filter((id) => id !== deliveryPricing.id)
                );
                setData(
                  getArrayWithoutObject(deliveriesPricings, deliveryPricing)
                );
                toast.success(i18next.t('view-restaurant-deliveries-pricings.remove-success'));
              })
              .catch(() => {
                setLoadingDeliveryPricings((prev) =>
                  prev.filter((id) => id !== deliveryPricing.id)
                );
                toast.error(i18next.t('view-restaurant-deliveries-pricings.remove-error'));
              });
          };

          return (
            <CommonSection header={i18next.t('view-restaurant-deliveries-pricings.header')}>
              {!!deliveriesPricings?.length ? (
                <DeliveriesPricingsList
                  deliveriesPricings={deliveriesPricings}
                  isLoading={isLoading}
                  onRemove={handleRemoveDeliveryPricing}
                />
              ) : (
                <div className={'h4 my-2 text-danger'}>
                  {i18next.t('view-restaurant-deliveries-pricings.no-pricings')}
                </div>
              )}
              <AddDeliveryPricingForm
                onAddDeliveryPricing={handleAddDeliveryPricing}
              />
            </CommonSection>
          );
        }}
      </UrlFetcher>
    </>
  );
};

export default ViewRestaurantDeliveriesPricings;
